import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Billing } from '@hero/schema';
import { cn } from '@hero/web-lib';
import {
  AlertCircleIcon,
  ArrowRightIcon,
  CircleCheckIcon,
  HourglassIcon,
  LoaderIcon,
  RefreshIcon,
} from '@hero/web-icons';
import { Button } from '../../../ui/Button/Button';
import { Price } from '../../../app/Price';

export type BillingCheckoutReturnState =
  | 'verifying'
  | 'success'
  | 'failed'
  | 'pending';

export interface BillingCheckoutReturnScreenProps {
  state: BillingCheckoutReturnState;
  /**
   * Parent billing for the BTx we're confirming — used to show the
   * amount paid + plan title on the success card. Missing while
   * `state === 'verifying'`.
   */
  billing?: Billing;
  /**
   * Gateway-provided failure message ("Card declined", "Insufficient
   * funds", etc.). Falls back to a generic copy when not set.
   */
  errorReason?: string;
  /** "Start working" CTA — navigates the user to the dashboard. */
  onStartWorking: () => void;
  /** "Back to billing" CTA — sends user to /billing overview. */
  onBackToBilling: () => void;
  /** "Try again" CTA on the failed state — opens the plan picker. */
  onTryAgain: () => void;
  /**
   * Optional manual refresh for the `pending` state. The page wires
   * this to refetch the lookup query.
   */
  onRefresh?: () => void;
  className?: string;
}

/**
 * Centered post-checkout confirmation screen.
 *
 * Renders one of four mutually exclusive states based on the BTx
 * status we polled on the page. Designed to feel like a "you're done"
 * moment — single column, big icon, big title, clear primary CTA —
 * not another piece of dashboard chrome.
 */
export function BillingCheckoutReturnScreen({
  state,
  billing,
  errorReason,
  onStartWorking,
  onBackToBilling,
  onTryAgain,
  onRefresh,
  className,
}: BillingCheckoutReturnScreenProps) {
  return (
    <div
      className={cn(
        'mx-auto flex w-full max-w-xl flex-col items-center gap-6 px-4 py-12 text-center',
        className,
      )}>
      {state === 'verifying' && <VerifyingCard />}
      {state === 'success' && (
        <SuccessCard
          billing={billing}
          onStartWorking={onStartWorking}
          onBackToBilling={onBackToBilling}
        />
      )}
      {state === 'failed' && (
        <FailedCard
          errorReason={errorReason}
          onTryAgain={onTryAgain}
          onBackToBilling={onBackToBilling}
        />
      )}
      {state === 'pending' && (
        <PendingCard
          onRefresh={onRefresh}
          onBackToBilling={onBackToBilling}
        />
      )}
    </div>
  );
}

function VerifyingCard() {
  const { t } = useTranslation('m-billing');
  return (
    <article className="flex w-full flex-col items-center gap-4 rounded-3xl border border-gray-100 bg-white p-10 shadow-sm">
      <span className="flex size-16 items-center justify-center rounded-full bg-gray-50 text-gray-500">
        <LoaderIcon className="size-8 animate-spin" />
      </span>
      <div className="flex flex-col gap-1.5">
        <h1 className="text-xl font-semibold text-gray-900">
          {t('checkout.return.verifying.title')}
        </h1>
        <p className="text-sm text-gray-500">
          {t('checkout.return.verifying.subtitle')}
        </p>
      </div>
    </article>
  );
}

interface SuccessCardProps {
  billing?: Billing;
  onStartWorking: () => void;
  onBackToBilling: () => void;
}

function SuccessCard({ billing, onStartWorking, onBackToBilling }: SuccessCardProps) {
  const { t } = useTranslation('m-billing');
  return (
    <article className="flex w-full flex-col items-center gap-6 rounded-3xl border border-emerald-100 bg-white p-10 shadow-sm">
      <span className="flex size-20 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-8 ring-emerald-50/40">
        <CircleCheckIcon className="size-10" />
      </span>
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-gray-900">
          {t('checkout.return.success.title')}
        </h1>
        <p className="text-sm text-gray-500">
          {t('checkout.return.success.subtitle')}
        </p>
      </div>

      {billing && (
        <div className="flex w-full flex-col items-center gap-1 rounded-2xl border border-gray-100 bg-gray-50/60 px-6 py-4">
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {t('checkout.return.success.amount_paid')}
          </span>
          <Price
            amount={billing.amount.total}
            currency={billing.amount.currency}
            size="xl"
            className="font-bold text-gray-900"
          />
          {billing.title && (
            <span className="text-sm text-gray-600">{billing.title}</span>
          )}
        </div>
      )}

      <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
        <Button size="lg" onClick={onStartWorking} className="w-full sm:w-auto">
          <span className="inline-flex items-center gap-2">
            {t('checkout.return.success.start_working')}
            <ArrowRightIcon className="size-4 rtl:rotate-180" />
          </span>
        </Button>
        <Button
          size="lg"
          variant="ghost"
          onClick={onBackToBilling}
          className="w-full sm:w-auto">
          {t('checkout.return.success.back_to_billing')}
        </Button>
      </div>
    </article>
  );
}

interface FailedCardProps {
  errorReason?: string;
  onTryAgain: () => void;
  onBackToBilling: () => void;
}

function FailedCard({ errorReason, onTryAgain, onBackToBilling }: FailedCardProps) {
  const { t } = useTranslation('m-billing');
  return (
    <article className="flex w-full flex-col items-center gap-6 rounded-3xl border border-rose-100 bg-white p-10 shadow-sm">
      <span className="flex size-20 items-center justify-center rounded-full bg-rose-50 text-rose-600 ring-8 ring-rose-50/40">
        <AlertCircleIcon className="size-10" />
      </span>
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-gray-900">
          {t('checkout.return.failed.title')}
        </h1>
        <p className="text-sm text-gray-500">
          {t('checkout.return.failed.subtitle')}
        </p>
      </div>

      <div className="w-full rounded-2xl border border-rose-100 bg-rose-50/60 px-4 py-3 text-start text-sm text-rose-700">
        {errorReason || t('checkout.return.failed.default_reason')}
      </div>

      <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
        <Button size="lg" onClick={onTryAgain} className="w-full sm:w-auto">
          <span className="inline-flex items-center gap-2">
            {t('checkout.return.failed.try_again')}
            <ArrowRightIcon className="size-4 rtl:rotate-180" />
          </span>
        </Button>
        <Button
          size="lg"
          variant="ghost"
          onClick={onBackToBilling}
          className="w-full sm:w-auto">
          {t('checkout.return.failed.back_to_billing')}
        </Button>
      </div>
    </article>
  );
}

interface PendingCardProps {
  onRefresh?: () => void;
  onBackToBilling: () => void;
}

function PendingCard({ onRefresh, onBackToBilling }: PendingCardProps) {
  const { t } = useTranslation('m-billing');
  return (
    <article className="flex w-full flex-col items-center gap-6 rounded-3xl border border-amber-100 bg-white p-10 shadow-sm">
      <span className="flex size-20 items-center justify-center rounded-full bg-amber-50 text-amber-600 ring-8 ring-amber-50/40">
        <HourglassIcon className="size-10" />
      </span>
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-gray-900">
          {t('checkout.return.pending.title')}
        </h1>
        <p className="text-sm text-gray-500">
          {t('checkout.return.pending.subtitle')}
        </p>
      </div>

      <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
        {onRefresh && (
          <Button size="lg" onClick={onRefresh} className="w-full sm:w-auto">
            <span className="inline-flex items-center gap-2">
              <RefreshIcon className="size-4" />
              {t('checkout.return.pending.refresh')}
            </span>
          </Button>
        )}
        <Button
          size="lg"
          variant="ghost"
          onClick={onBackToBilling}
          className="w-full sm:w-auto">
          {t('checkout.return.pending.back_to_billing')}
        </Button>
      </div>
    </article>
  );
}
